import type * as TooltipPrimitive from '@radix-ui/react-tooltip';
import type * as React from 'react';
import { useCallback, useState } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
} from './Tooltip';

interface ReusableTooltipProps {
  content: React.ReactNode;
  children?: React.ReactNode;
  delayDuration?: number;
  className?: string;
  disabled?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  triggerProps?: TooltipPrimitive.TooltipTriggerProps &
    React.RefAttributes<HTMLButtonElement>;
  contentProps?: TooltipPrimitive.TooltipContentProps &
    React.RefAttributes<HTMLDivElement>;
  position?: 'top' | 'bottom' | 'left' | 'right';
  align?: 'start' | 'center' | 'end';
}

interface PersistentTooltipProps extends ReusableTooltipProps {
  persistent?: boolean;
}

const ReusableTooltip = ({
  content,
  children,
  delayDuration = 300,
  className,
  disabled,
  position = 'top',
  open,
  onOpenChange,
  triggerProps,
  contentProps,
  align,
}: ReusableTooltipProps) => {
  if (disabled) return children;

  return (
    <TooltipProvider delayDuration={delayDuration}>
      <Tooltip open={open} onOpenChange={onOpenChange}>
        <TooltipTrigger {...triggerProps}>{children}</TooltipTrigger>
        <TooltipPortal>
          <TooltipContent
            className={className}
            side={position}
            sideOffset={4}
            align={align}
            {...contentProps}
          >
            {content}
          </TooltipContent>
        </TooltipPortal>
      </Tooltip>
    </TooltipProvider>
  );
};

const PersistentTooltip = ({
  content,
  children,
  delayDuration = 300,
  className,
  disabled,
  position = 'top',
  persistent = false,
}: PersistentTooltipProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
    setIsOpen(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    setIsOpen(false);
  }, []);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (isHovered) {
        setIsOpen(true);
      } else {
        setIsOpen(open);
      }
    },
    [isHovered],
  );

  if (disabled) return children;

  if (!persistent) {
    return (
      <ReusableTooltip
        content={content}
        delayDuration={delayDuration}
        className={className}
        position={position}
      >
        {children}
      </ReusableTooltip>
    );
  }

  return (
    <TooltipProvider delayDuration={delayDuration}>
      <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <Tooltip open={isOpen} onOpenChange={handleOpenChange}>
          <TooltipTrigger asChild>{children}</TooltipTrigger>
          <TooltipContent className={className} side={position} sideOffset={4}>
            {content}
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
};

export { ReusableTooltip, PersistentTooltip };
